import React, { useCallback, useState, createContext, useContext } from 'react';
type Lang = 'en' | 'es';
interface LanguageContextType {
  lang: Lang;
  toggleLang: () => void;
  t: (key: string) => string;
}
const translations: Record<string, Record<Lang, string>> = {
  // Navbar
  'nav.team': {
    en: 'Team',
    es: 'Equipo'
  },
  'nav.matches': {
    en: 'Matches',
    es: 'Partidos'
  },
  'nav.deals': {
    en: 'Deals',
    es: 'Ofertas'
  },
  'nav.liveScores': {
    en: 'Live Scores',
    es: 'Resultados en Vivo'
  },
  'nav.orderYours': {
    en: 'Order Yours',
    es: 'Compra la Tuya'
  },
  // Hero
  'hero.badge': {
    en: "You're now part of USA 2026",
    es: 'Ahora eres parte de USA 2026'
  },
  'hero.title1': {
    en: "Don't Just Support the USA,",
    es: 'No Solo Apoyes a USA,'
  },
  'hero.title2': {
    en: 'Live It',
    es: 'Vívelo'
  },
  'hero.subtitle': {
    en: 'Updated in real live time. Access exclusive content, match updates, and travel deals directly from your digital hub.',
    es: 'Actualizado en tiempo real. Accede a contenido exclusivo, actualizaciones de partidos y ofertas de viaje directamente desde tu hub digital.'
  },
  'hero.cta1': {
    en: 'Watch Matches',
    es: 'Ver Partidos'
  },
  'hero.cta2': {
    en: 'Explore Team',
    es: 'Explorar Equipo'
  },
  'hero.scroll': {
    en: 'Scroll to discover',
    es: 'Desliza para descubrir'
  },
  // Team Overview
  'team.label': {
    en: 'Our Legacy',
    es: 'Nuestro Legado'
  },
  'team.title1': {
    en: 'A Nation Forged',
    es: 'Una Nación Forjada'
  },
  'team.title2': {
    en: 'In Glory',
    es: 'En la Gloria'
  },
  'team.desc': {
    en: 'The USMNT represents more than just soccer; it embodies the passion, resilience, and spirit of a nation. From the historic 1930 run to conquering the Nations League, the Stars and Stripes continue to write their name in history.',
    es: 'El USMNT representa más que solo fútbol; encarna la pasión, resiliencia y espíritu de una nación. Desde la histórica campaña de 1930 hasta conquistar la Nations League, las Barras y Estrellas continúan escribiendo su nombre en la historia.'
  },
  'team.since': {
    en: 'Since 1916',
    es: 'Desde 1916'
  },
  'team.history': {
    en: 'Explore Our Full History →',
    es: 'Explora Nuestra Historia Completa →'
  },
  'team.euro': {
    en: 'Gold Cup',
    es: 'Copa Oro'
  },
  'team.euroDesc': {
    en: '7-Time Champions',
    es: '7 Veces Campeones'
  },
  'team.nationsLeague': {
    en: 'Concacaf Nations League',
    es: 'Liga de Naciones Concacaf'
  },
  'team.nationsLeagueDesc': {
    en: '3-Time Winners',
    es: '3 Veces Ganadores'
  },
  'team.euros': {
    en: '11 World Cups',
    es: '11 Mundiales'
  },
  'team.eurosDesc': {
    en: 'Global Stage Appearances',
    es: 'Apariciones en el Escenario Global'
  },
  'team.worldCups': {
    en: '3rd Place',
    es: '3er Lugar'
  },
  'team.worldCupsDesc': {
    en: '1930 World Cup Best',
    es: 'Mejor Resultado en Mundial 1930'
  },
  'team.videoLabel': {
    en: 'We Are World Cup 2026',
    es: 'Somos Copa Mundial 2026'
  },
  'team.videoTitle1': {
    en: 'This Is Our',
    es: 'Este Es Nuestro'
  },
  'team.videoTitle2': {
    en: 'Moment',
    es: 'Momento'
  },
  // Key Players
  'players.label': {
    en: 'The Squad',
    es: 'La Plantilla'
  },
  'players.title1': {
    en: 'The Stars of',
    es: 'Las Estrellas de'
  },
  'players.title2': {
    en: 'USA',
    es: 'USA'
  },
  'players.viewSquad': {
    en: 'View Full Squad on ESPN.com',
    es: 'Ver Plantilla Completa en ESPN.com'
  },
  'players.forward': {
    en: 'Forward',
    es: 'Delantero'
  },
  'players.midfielder': {
    en: 'Midfielder',
    es: 'Mediocampista'
  },
  'players.defender': {
    en: 'Defender',
    es: 'Defensa'
  },
  // Match Center
  'matches.label': {
    en: 'World Cup 2026',
    es: 'Mundial 2026'
  },
  'matches.title1': {
    en: 'Match',
    es: 'Centro de'
  },
  'matches.title2': {
    en: 'Center',
    es: 'Partidos'
  },
  'matches.desc': {
    en: 'Follow the USMNT as hosts in Group D. Get live updates, official lineups, and real-time scores from the biggest stage in soccer.',
    es: 'Sigue al USMNT como anfitriones en el Grupo D. Obtén actualizaciones en vivo, alineaciones oficiales y resultados en tiempo real del mayor escenario del fútbol.'
  },
  'matches.standings': {
    en: 'Group Standings',
    es: 'Clasificación del Grupo'
  },
  'matches.live': {
    en: 'Live',
    es: 'En Vivo'
  },
  'matches.autoRefresh': {
    en: 'Auto-refreshes every 5 min',
    es: 'Se actualiza automáticamente cada 5 min'
  },
  'matches.refreshNow': {
    en: 'Refresh Now',
    es: 'Actualizar Ahora'
  },
  'matches.team': {
    en: 'Team',
    es: 'Equipo'
  },
  'matches.viewStandings': {
    en: 'View Live Standings on FlashScore',
    es: 'Ver Clasificación en Vivo en FlashScore'
  },
  'matches.nextMatch': {
    en: 'Next Match',
    es: 'Próximo Partido'
  },
  'matches.espn': {
    en: 'ESPN Schedule',
    es: 'Calendario ESPN'
  },
  'matches.sofascore': {
    en: 'SofaScore',
    es: 'SofaScore'
  },
  'matches.flashscore': {
    en: 'FlashScore Live',
    es: 'FlashScore en Vivo'
  },
  'matches.foxsports': {
    en: 'Fox Sports',
    es: 'Fox Sports'
  },
  // Stats
  'stats.label': {
    en: 'USA By The Numbers',
    es: 'USA en Números'
  },
  'stats.worldCup': {
    en: 'World Cup Appearances',
    es: 'Apariciones en Mundiales'
  },
  'stats.titles': {
    en: 'Major Titles',
    es: 'Títulos Principales'
  },
  'stats.goals': {
    en: 'International Goals',
    es: 'Goles Internacionales'
  },
  'stats.ronaldo': {
    en: 'Pulisic Goals',
    es: 'Goles de Pulisic'
  },
  // Historic Moments
  'history.label': {
    en: 'The Archives',
    es: 'Los Archivos'
  },
  'history.title1': {
    en: 'Moments That',
    es: 'Momentos Que'
  },
  'history.title2': {
    en: 'Made History',
    es: 'Hicieron Historia'
  },
  'history.upset': {
    en: 'USA 1-0 England 1950',
    es: 'USA 1-0 Inglaterra 1950'
  },
  'history.donovan': {
    en: 'Donovan vs Algeria 2010',
    es: 'Donovan vs Argelia 2010'
  },
  'history.howard': {
    en: 'History — FIFA World Cup USA 1994 Intro',
    es: 'Historia — Intro Copa Mundial FIFA USA 1994'
  },
  'history.nationsLeague': {
    en: 'USA: All FIFA World Cup Goals 2010-2022',
    es: 'USA: Todos los Goles en Mundiales FIFA 2010-2022'
  },
  'history.pulisic': {
    en: 'USA v Portugal — FIFA World Cup 2002',
    es: 'USA v Portugal — Copa Mundial FIFA 2002'
  },
  'history.run2002': {
    en: 'Mexico vs USA — FIFA World Cup 2002',
    es: 'México vs USA — Copa Mundial FIFA 2002'
  },
  'history.dempsey': {
    en: 'Dempsey Chip vs Juventus',
    es: 'Vaselina de Dempsey vs Juventus'
  },
  'history.goldCup': {
    en: 'Gold Cup 2021 Champions',
    es: 'Campeones Copa Oro 2021'
  },
  'history.hosts': {
    en: 'USA: All FIFA World Cup Goals 1990-2006',
    es: 'USA: Todos los Goles en Mundiales FIFA 1990-2006'
  },
  'history.beasley': {
    en: 'DaMarcus Beasley Legacy',
    es: 'Legado de DaMarcus Beasley'
  },
  // Fan Experience
  'fan.label': {
    en: 'More Than A Game',
    es: 'Más Que Un Juego'
  },
  'fan.title1': {
    en: 'Feel the Passion of',
    es: 'Siente la Pasión de'
  },
  'fan.title2': {
    en: 'USA',
    es: 'USA'
  },
  'fan.desc': {
    en: 'Join millions of voices singing as one. Wear the colors, feel the history, and be part of the journey to global glory on home soil.',
    es: 'Únete a millones de voces cantando como una sola. Viste los colores, siente la historia y sé parte del viaje hacia la gloria mundial en casa.'
  },
  // Tickets / Travel Deals
  'deals.label': {
    en: 'Travel to the World Cup',
    es: 'Viaja al Mundial'
  },
  'deals.title1': {
    en: 'Exclusive',
    es: 'Ofertas Exclusivas'
  },
  'deals.title2': {
    en: 'Fan Deals',
    es: 'para Fans'
  },
  'deals.desc': {
    en: 'Exclusive discounts for USA fans on tickets, hotels, flights & transport to support the USMNT across North America.',
    es: 'Descuentos exclusivos para fans de USA en entradas, hoteles, vuelos y transporte para apoyar al USMNT en toda Norteamérica.'
  },
  'deals.unlockVip': {
    en: 'Unlock VIP Discounts',
    es: 'Desbloquea Descuentos VIP'
  },
  'deals.applyCode': {
    en: 'Apply code at checkout on partner sites',
    es: 'Aplica el código en el checkout de sitios asociados'
  },
  'deals.tickets': {
    en: 'Match Tickets',
    es: 'Entradas para Partidos'
  },
  'deals.ticketsDesc': {
    en: 'Up to 15% off group stage packages',
    es: 'Hasta 15% de descuento en paquetes de fase de grupos'
  },
  'deals.findTickets': {
    en: 'Find Tickets',
    es: 'Encontrar Entradas'
  },
  'deals.hotels': {
    en: 'Hotels & Accommodation',
    es: 'Hoteles y Alojamiento'
  },
  'deals.hotelsDesc': {
    en: 'Partner hotel deals near match venues',
    es: 'Ofertas de hoteles asociados cerca de los estadios'
  },
  'deals.bookHotels': {
    en: 'Book Hotels',
    es: 'Reservar Hoteles'
  },
  'deals.flights': {
    en: 'Domestic Flights',
    es: 'Vuelos Nacionales'
  },
  'deals.flightsDesc': {
    en: 'Special fares between host cities',
    es: 'Tarifas especiales entre ciudades anfitrionas'
  },
  'deals.searchFlights': {
    en: 'Search Flights',
    es: 'Buscar Vuelos'
  },
  'deals.transport': {
    en: 'Local Transport',
    es: 'Transporte Local'
  },
  'deals.transportDesc': {
    en: 'Shuttle services & car rentals at match cities',
    es: 'Servicios de transporte y alquiler de autos en ciudades sede'
  },
  'deals.planTransport': {
    en: 'Plan Transport',
    es: 'Planear Transporte'
  },
  // Fan HQ / Gallery
  'fanHq.label': {
    en: 'Everything You Need for USA 2026',
    es: 'Todo lo Que Necesitas para USA 2026'
  },
  'fanHq.title1': {
    en: 'Fan',
    es: 'QG del'
  },
  'fanHq.title2': {
    en: 'HQ',
    es: 'Fan'
  },
  'fanHq.countdown': {
    en: "Countdown to USA's First Match",
    es: 'Cuenta Regresiva para el Primer Partido de USA'
  },
  'fanHq.days': {
    en: 'Days',
    es: 'Días'
  },
  'fanHq.hrs': {
    en: 'Hrs',
    es: 'Hrs'
  },
  'fanHq.min': {
    en: 'Min',
    es: 'Min'
  },
  'fanHq.sec': {
    en: 'Sec',
    es: 'Seg'
  },
  'fanHq.capacity': {
    en: 'Capacity',
    es: 'Capacidad'
  },
  'fanHq.chants': {
    en: 'Learn the',
    es: 'Aprende los'
  },
  'fanHq.chantsHighlight': {
    en: 'Chants',
    es: 'Cánticos'
  },
  'fanHq.nationalAnthem': {
    en: 'National Anthem',
    es: 'Himno Nacional'
  },
  'fanHq.classicChant': {
    en: 'Classic Stadium Chant',
    es: 'Cántico Clásico de Estadio'
  },
  'fanHq.crowdFavorite': {
    en: 'Crowd Favorite',
    es: 'Favorito de la Multitud'
  },
  'fanHq.victoryChant': {
    en: 'Victory Chant',
    es: 'Cántico de Victoria'
  },
  'fanHq.watchYoutube': {
    en: 'Watch on YouTube',
    es: 'Ver en YouTube'
  },
  'fanHq.phrases': {
    en: 'Essential',
    es: 'Frases'
  },
  'fanHq.phrasesHighlight': {
    en: 'Phrases',
    es: 'Esenciales'
  },
  'fanHq.quickLinks': {
    en: 'Quick',
    es: 'Enlaces'
  },
  'fanHq.quickLinksHighlight': {
    en: 'Links',
    es: 'Rápidos'
  },
  'fanHq.whereToWatch': {
    en: 'Where to Watch',
    es: 'Dónde Ver'
  },
  'fanHq.whereToWatchDesc': {
    en: 'Streaming & TV channels',
    es: 'Canales de streaming y TV'
  },
  'fanHq.fanZones': {
    en: 'Fan Info Channel',
    es: 'Canal de Info para Fans'
  },
  'fanHq.fanZonesDesc': {
    en: 'Official FIFA fan information',
    es: 'Información oficial FIFA para fans'
  },
  'fanHq.fifaRanking': {
    en: 'FIFA Ranking',
    es: 'Ranking FIFA'
  },
  'fanHq.fifaRankingDesc': {
    en: 'Official world rankings',
    es: 'Ranking mundial oficial'
  },
  'fanHq.fullSchedule': {
    en: 'Full WC Schedule',
    es: 'Calendario Completo del Mundial'
  },
  'fanHq.fullScheduleDesc': {
    en: 'All 104 matches',
    es: 'Todos los 104 partidos'
  },
  // Footer
  'footer.desc': {
    en: 'An official Qtap by STDI digital product. Your smart NFC T-shirt hub for USA 2026 — real-time match data, exclusive content, and travel deals at your fingertips.',
    es: 'Un producto digital oficial de Qtap by STDI. Tu hub de camiseta NFC inteligente para USA 2026 — datos de partidos en tiempo real, contenido exclusivo y ofertas de viaje a tu alcance.'
  },
  'footer.resources': {
    en: 'Resources',
    es: 'Recursos'
  },
  'footer.team': {
    en: 'Team',
    es: 'Equipo'
  },
  'footer.firstTeam': {
    en: 'First Team',
    es: 'Primer Equipo'
  },
  'footer.matches': {
    en: 'Matches',
    es: 'Partidos'
  },
  'footer.standings': {
    en: 'Standings',
    es: 'Clasificación'
  },
  'footer.travelDeals': {
    en: 'Travel Deals',
    es: 'Ofertas de Viaje'
  },
  'footer.support': {
    en: 'Support',
    es: 'Soporte'
  },
  'footer.contactUs': {
    en: 'Contact Us',
    es: 'Contáctanos'
  },
  'footer.faq': {
    en: 'FAQ',
    es: 'Preguntas Frecuentes'
  },
  'footer.privacy': {
    en: 'Privacy Policy',
    es: 'Política de Privacidad'
  },
  'footer.terms': {
    en: 'Terms of Service',
    es: 'Términos de Servicio'
  },
  'footer.cookies': {
    en: 'Cookie Policy',
    es: 'Política de Cookies'
  },
  'footer.copyright': {
    en: 'Qtap by STDI. All rights reserved.',
    es: 'Qtap by STDI. Todos los derechos reservados.'
  }
};
const LanguageContext = createContext<LanguageContextType>({
  lang: 'en',
  toggleLang: () => {},
  t: (key: string) => key
});
export function LanguageProvider({
  children
}: {
  children: React.ReactNode;
}) {
  const [lang, setLang] = useState<Lang>('en');
  const toggleLang = useCallback(() => {
    setLang(prev => prev === 'en' ? 'es' : 'en');
  }, []);
  const t = useCallback((key: string) => {
    return translations[key]?.[lang] || key;
  }, [lang]);
  return <LanguageContext.Provider value={{
    lang,
    toggleLang,
    t
  }} data-id="element-210">
      {children}
    </LanguageContext.Provider>;
}
export function useLanguage() {
  return useContext(LanguageContext);
}